import React, { Component, ErrorInfo, ReactNode } from "react";
import styles from "./ErrorBoundary.module.scss";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // Log to error reporting service if available
    if (import.meta.env.PROD) {
      // Add error reporting service integration here (e.g., Sentry)
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isZh = window.location.pathname.startsWith('/zh') || localStorage.getItem('language') === 'zh';
      
      return (
        <div className={styles.errorBoundary}>
          <div className={styles.errorContent}>
            <h1 className={styles.errorTitle}>{isZh ? "哎呀！出了點問題" : "Oops! Something went wrong"}</h1>
            <p className={styles.errorMessage}>
              {isZh ? "很抱歉造成您的不便，發生了未預期的錯誤。" : "We're sorry for the inconvenience. The application encountered an unexpected error."}
            </p>

            {import.meta.env.DEV && this.state.error && (
              <details className={styles.errorDetails}>
                <summary>{isZh ? "錯誤詳情（僅限開發階段）" : "Error Details (Development Only)"}</summary>
                <pre className={styles.errorStack}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className={styles.errorActions}>
              <button onClick={this.handleReset} className={styles.retryButton}>
                {isZh ? "再試一次" : "Try Again"}
              </button>
              <button
                onClick={() => (window.location.href = isZh ? "/zh" : "/")}
                className={styles.homeButton}
              >
                {isZh ? "回首頁" : "Go to Home"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
